import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { TimelineEntry } from '../../types/timeline';
import MarkdownHelp from '../ui/MarkdownHelp';

interface TimelineEntryFormProps {
  contactId: string;
  entry?: TimelineEntry;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function TimelineEntryForm({ contactId, entry, onSuccess, onCancel }: TimelineEntryFormProps) {
  const [date, setDate] = useState(
    entry ? new Date(entry.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [content, setContent] = useState(entry?.content || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (entry) {
        const { error } = await supabase
          .from('nrm_timeline_entries')
          .update({
            date: new Date(date + 'T00:00:00Z'),
            content,
            updated_at: new Date()
          })
          .eq('id', entry.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('nrm_timeline_entries')
          .insert([{
            contact_id: contactId,
            date: new Date(date + 'T00:00:00Z'),
            content
          }]);

        if (error) throw error;
      }

      onSuccess();
    } catch (err) {
      console.error('Failed to save timeline entry:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-none space-y-4">
        <div>
          <label htmlFor="date" className="block text-sm font-medium text-gray-300 mb-1">
            Date
          </label>
          <input
            type="date"
            id="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="input w-full"
            required
          />
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-300 mb-1">
            Content (Markdown supported)
            <div className="mt-1">
              <MarkdownHelp />
            </div>
          </label>
        </div>
      </div>

      <div className="flex-grow flex flex-col min-h-0 my-4">
        <textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="input w-full flex-grow"
          placeholder="Enter your notes here..."
          required
        />
      </div>

      <div className="flex-none flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? 'Saving...' : entry ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}